import { useState } from 'react';
import { Button, DrawerProps, message, Space, Table } from 'antd';
import { antdDrawer, useModal, create } from '@ebay/nice-modal-react';
import { CustomDrawer } from '@isocel/front-common';
import moment from 'moment';

import { InventoryStatus, PackageInventoryItem } from '../../queries/api/types';
import { updateInventoryItemStatus } from '../../queries/api/store-inventory';
import './InventoryHistory.less';

interface InventoryDetailsData {
    date: string;
    storeName: string;
    storeUserName: string;
    packagesInventory: PackageInventoryItem[];
    packageLabelById: Map<string, string>;
    packageCodeById: Map<string, string>;
}

type Props = DrawerProps & {
    data: InventoryDetailsData;
    // Hub validation: shows Accepter / Refuser buttons
    validationMode?: boolean;
    onStatusChanged?: () => void;
};

const InventoryDetailsDrawer = create<Props>(({ data, validationMode, onStatusChanged, ...props }) => {
    const modal = useModal();
    const [items, setItems] = useState<PackageInventoryItem[]>(data.packagesInventory);
    const [updatingKey, setUpdatingKey] = useState<string | null>(null);
    const [bulkUpdating, setBulkUpdating] = useState(false);

    const itemKey = (item: PackageInventoryItem) => `${item.inventoryId ?? ''}-${item.package}`;

    const handleStatusChange = async (item: PackageInventoryItem, status: InventoryStatus) => {
        if (!item.inventoryId) return;
        setUpdatingKey(itemKey(item));
        try {
            await updateInventoryItemStatus({ inventoryId: item.inventoryId, packageId: item.package, status });
            setItems((prev) => prev.map((i) => (itemKey(i) === itemKey(item) ? { ...i, status } : i)));
            onStatusChanged?.();
        } catch {
            message.error('Erreur lors de la mise à jour du statut.');
        } finally {
            setUpdatingKey(null);
        }
    };

    const handleBulkStatusChange = async (status: InventoryStatus) => {
        const targets = items.filter((i) => i.inventoryId && i.status !== status);
        if (targets.length === 0) return;
        setBulkUpdating(true);
        const updated = new Set<string>();
        // Sequential calls: several lines can belong to the same inventory document
        for (const item of targets) {
            try {
                await updateInventoryItemStatus({
                    inventoryId: item.inventoryId as string,
                    packageId: item.package,
                    status,
                });
                updated.add(itemKey(item));
            } catch {
                // reported below
            }
        }
        setItems((prev) => prev.map((i) => (updated.has(itemKey(i)) ? { ...i, status } : i)));
        if (updated.size > 0) onStatusChanged?.();
        if (updated.size < targets.length) {
            message.error(`${targets.length - updated.size} ligne(s) n'ont pas pu être mises à jour.`);
        } else {
            message.success(`${updated.size} ligne(s) mise(s) à jour.`);
        }
        setBulkUpdating(false);
    };

    const handleClose = () => {
        modal.hide();
    };

    const formattedDate = data.date
        ? new Date(data.date).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          })
        : '';

    const sortedPackages = [...items].sort((a, b) => {
        const dateA = a.inventoryDate || '';
        const dateB = b.inventoryDate || '';
        const dateCmp = dateA.localeCompare(dateB);
        if (dateCmp !== 0) return dateCmp;
        const codeA = data.packageCodeById.get(a.package) || '';
        const codeB = data.packageCodeById.get(b.package) || '';
        return codeA.localeCompare(codeB, undefined, { numeric: true });
    });

    const columns = [
        {
            title: 'Date & Heure',
            dataIndex: 'inventoryDate',
            key: 'inventoryDate',
            render: (value: string) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : ''),
        },
        {
            title: 'Code emballage',
            dataIndex: 'package',
            key: 'code',
            render: (value: string) => data.packageCodeById.get(value) || value,
        },
        {
            title: 'Emballage',
            dataIndex: 'package',
            key: 'package',
            render: (value: string) => data.packageLabelById.get(value) || value,
        },
        {
            title: 'Quantité comptée',
            dataIndex: 'countedValue',
            key: 'countedValue',
        },
        {
            title: 'Écart constaté',
            dataIndex: 'actualDifference',
            key: 'actualDifference',
            render: (value: number) => {
                const cls = value === 0 ? 'ecart-zero' : value > 0 ? 'ecart-positive' : 'ecart-negative';
                return <span className={cls}>{value > 0 ? `+${value}` : value}</span>;
            },
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (value?: InventoryStatus) => value || '-',
        },
        ...(validationMode
            ? [
                  {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: unknown, item: PackageInventoryItem) => {
                          const canAccept = item.status !== InventoryStatus.accepted;
                          const canRefuse = item.status !== InventoryStatus.refused;
                          const loading = updatingKey === itemKey(item);
                          return (
                              <Space>
                                  {canAccept && (
                                      <Button
                                          size="small"
                                          type="primary"
                                          loading={loading}
                                          disabled={bulkUpdating || (!!updatingKey && !loading)}
                                          onClick={async () => await handleStatusChange(item, InventoryStatus.accepted)}
                                      >
                                          Accepter
                                      </Button>
                                  )}
                                  {canRefuse && (
                                      <Button
                                          size="small"
                                          type="primary"
                                          loading={loading}
                                          disabled={bulkUpdating || (!!updatingKey && !loading)}
                                          onClick={async () => await handleStatusChange(item, InventoryStatus.refused)}
                                      >
                                          Refuser
                                      </Button>
                                  )}
                              </Space>
                          );
                      },
                  },
              ]
            : []),
    ];

    return (
        <CustomDrawer
            width={validationMode ? 950 : 800}
            title={`Inventaire du ${formattedDate}${data.storeName ? ` - ${data.storeName}` : ''}`}
            {...props}
            {...antdDrawer(modal)}
            onClose={handleClose}
        >
            <div>
                <div className="flex justify-between context" style={{ marginBottom: 16 }}>
                    <div className="info-item">
                        <b>Date :</b> {formattedDate}
                    </div>
                    {data.storeName && (
                        <div className="info-item">
                            <b>Magasin :</b> {data.storeName}
                        </div>
                    )}
                    <div className="info-item">
                        <b>Utilisateur :</b> {data.storeUserName}
                    </div>
                    <div className="info-item">
                        <b>Nb emballages :</b> {items.length}
                    </div>
                </div>

                <h3>Détail des emballages inventoriés :</h3>
                <Table
                    columns={columns}
                    dataSource={sortedPackages}
                    rowKey={(item, idx) => `${item.package}-${String(idx)}`}
                    pagination={false}
                    size="small"
                />

                <Space style={{ marginTop: 16 }}>
                    {validationMode && (
                        <>
                            <Button
                                type="primary"
                                loading={bulkUpdating}
                                disabled={!!updatingKey || !items.some((i) => i.status !== InventoryStatus.accepted)}
                                onClick={async () => await handleBulkStatusChange(InventoryStatus.accepted)}
                            >
                                Tout accepter
                            </Button>
                            <Button
                                type="primary"
                                loading={bulkUpdating}
                                disabled={!!updatingKey || !items.some((i) => i.status !== InventoryStatus.refused)}
                                onClick={async () => await handleBulkStatusChange(InventoryStatus.refused)}
                            >
                                Tout refuser
                            </Button>
                        </>
                    )}
                    <Button type="primary" onClick={handleClose}>
                        Retour
                    </Button>
                </Space>
            </div>
        </CustomDrawer>
    );
});

export default InventoryDetailsDrawer;
